
import {FormEnvironment} from "@fd/Types.ts";
import {computed, ref, type ComputedRef, reactive} from "vue";
import {storeToRefs} from "pinia";
import {useMailMapStore} from "@fd/editors/mail-select/MailMapStore.ts";
import {InternetAddress, parseAddress} from "@t/mail/InternetAddress.ts";

export interface MSegment {
	key: string;
	type: "mail" | "query";
	index: number;
}

export interface MailSegment extends MSegment, InternetAddress {
	type: "mail";
	selected?: boolean;
}

export interface QuerySegment extends MSegment {
	type: "query";
	/** This query is the last editor following all tags */
	last?: boolean;
	query: string
}

export interface SegmentWrapPair {
	key: string;
	q?: QuerySegment;
	p?: MailSegment;
}


export function useMailSegmentTags(env:FormEnvironment, array:ComputedRef<(InternetAddress|string)[]>) {

	const selected = reactive(new Set<string>());

	const mailTags = computed<MailSegment[]>(() => {
		const ps:MailSegment[] = [];
		if (Array.isArray(array.value)) {
			for (let i = 0; i < array.value.length; i++) {
				const val = array.value[i];
				let ia: InternetAddress | null = null;
				if (typeof val === 'string') ia = parseAddress(val);
				else if (typeof val === 'object' && Object.hasOwn(val, "address")) ia = val as InternetAddress;
				if (!ia) {
					// Invalid address
					ia = {personal: val == null ? "NULL" : val.toString(), address: ""};
				}
				ps.push({type: "mail", index: i, key: ia.address, selected: selected.has(ia.address.toLowerCase()), ...ia});
			}
		}
		return ps;
	});

	/** Return all e-mail addresses inside the editor */
	const allAddresses = computed<string[]>(() => {
		const a:string[] = [];
		for (let mt of mailTags.value) {
			if (mt.address) a.push(mt.address);
		}
		return a;
	});
	
	/**
	 * Number of mails in the array
	 **/
	const count = computed<number>(() => {
		return Array.isArray(array.value) ? array.value.length : 0;
	});

	/** Return true if mail alrady inserted in data */
	function contains(address:string):boolean {
		const lcAddr = address.toLowerCase();
		for (let item of mailTags.value) {
			if (item.address.toLowerCase() === lcAddr) return true;
		}
		return false;
	}

	function selectEmail(address:string):void {
		selected.add(address.toLowerCase());
	}

	function toggleEmailSelection(address:string):void {
		let lc = address.toLowerCase();
		if (selected.has(lc)) selected.delete(lc);
		else selected.add(lc);
	}

	function unselectEmail(address:string):void {
		selected.delete(address.toLowerCase());
	}

	function selectAll():void {
		selected.clear();
		for (let mt of mailTags.value) {
			if (mt.address) selectEmail(mt.address);
		}
	}

	function unselectAll() {
		selected.clear();
	}

	return { mailTags, contains, count, selectEmail, unselectEmail, selectAll, unselectAll, toggleEmailSelection, allAddresses };
}